import { faChartPieSimple, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { IconDefinition } from '@fortawesome/pro-thin-svg-icons';
import { Chip, Typography } from '@mui/material';
import { FontIcon } from '@resurs-internal/mui';
import { useEffect, useRef, useState } from 'react';

import { ComplementaryConsolidation } from '../../api/complementaryConsolidation';
import { validateKidService } from '../../api/KidValidation';
import { Coins } from '../../assets/svg/Coins';
import Button from '../../components/Button/Button';
import Group from '../../components/Group/Group';
import Icon from '../../components/Icon/Icon';
import IntegerField from '../../components/IntegerField/IntegerField';
import Spinner from '../../components/Spinner/Spinner';
import TextInputField from '../../components/TextInputField/TextInputField';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useConsolidationTexts } from '../../hooks/useConsolidationTexts';
import { FieldValidationProps, ValidationResultType } from '../../hooks/useFieldValidation';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldStateByName } from '../../store/applicationform/applicationFormSelectors';
import { IFieldState } from '../../store/applicationform/applicationFormTypes';
import { Consolidation } from '../../types/ConsolidationTypes';
import {
    CardContainer,
    CardHeader,
    CardHeaderLeft,
    CardTextWrapper,
    SaveButtonWrapper,
} from './ConsolidationLoanCard.styles';

type Props = {
    consolidation: Consolidation;
    isSaved: boolean;
    onSave: (internalId: string) => Promise<void>;
    onUnsave: (internalId: string) => void;
    finalComplementaryConsolidations: ComplementaryConsolidation[];
    setSaveError: (hasError: boolean) => void;
};

const ConsolidationLoanCard = ({
    consolidation,
    isSaved,
    onSave,
    onUnsave,
    finalComplementaryConsolidations,
    setSaveError,
}: Props) => {
    const translate = useTranslation();
    const dispatch = useAppDispatch();
    const { proxyBaseUrl, apiKey } = useSettings();
    const { getTitle, getCaption } = useConsolidationTexts();
    const [expanded, setExpanded] = useState(!isSaved);
    const [isSaving, setIsSaving] = useState(false);
    const isSavingRef = useRef(false);
    const finalComplementaryConsolidationsRef = useRef(finalComplementaryConsolidations);

    const accountNumberFieldName = `consolidation_${consolidation.internalId};accountNumber`;
    const kidNumberFieldName = `consolidation_${consolidation.internalId};kidNumber`;
    const creditorNameFieldName = `consolidation_${consolidation.internalId};creditorName`;

    const getFieldValue = useAppSelector(selectFieldStateByName);

    const hasFieldError =
        !!getFieldValue(accountNumberFieldName)?.hasError ||
        !!getFieldValue(kidNumberFieldName)?.hasError ||
        (!consolidation.creditorName && !!getFieldValue(creditorNameFieldName)?.hasError);

    useEffect(() => {
        finalComplementaryConsolidationsRef.current = finalComplementaryConsolidations;
    }, [finalComplementaryConsolidations]);

    useEffect(() => {
        setExpanded(!isSaved);
    }, [isSaved]);

    const isPartial = consolidation.consolidationType === 'PARTIAL';

    const handleHeaderClick = async () => {
        if (isSavingRef.current) return;
        if (isSaved) {
            onUnsave(consolidation.internalId);
        } else if (expanded) {
            await handleSave();
        } else {
            setExpanded(true);
        }
    };

    const handleSave = async () => {
        if (isSavingRef.current) return;
        if (hasFieldError) return;

        const accountField = getFieldValue(accountNumberFieldName);
        const kidField = getFieldValue(kidNumberFieldName);
        const accountValue = String(accountField?.value || '');
        const kidValue = String(kidField?.value || '');
        let isValid = true;

        if (!accountValue || !/^\d{11}$/.test(accountValue)) {
            dispatch(
                addOrUpdateFormField({
                    fieldName: accountNumberFieldName,
                    value: accountValue,
                    hasError: true,
                    errorMessage: accountValue
                        ? translate('validation.error.account-number.format')
                        : translate('validation.account-number.required'),
                } as IFieldState),
            );
            isValid = false;
        } else if (accountField?.hasError) {
            isValid = false;
        }

        if (kidValue && !/^\d{4,25}$/.test(kidValue)) {
            dispatch(
                addOrUpdateFormField({
                    fieldName: kidNumberFieldName,
                    value: kidValue,
                    hasError: true,
                    errorMessage: translate('validation.error.consolidation.kid-number.format'),
                } as IFieldState),
            );
            isValid = false;
        } else if (kidField?.hasError) {
            isValid = false;
        }

        if (!consolidation.creditorName) {
            const creditorField = getFieldValue(creditorNameFieldName);
            const creditorValue = String(creditorField?.value || '');
            if (!creditorValue) {
                dispatch(
                    addOrUpdateFormField({
                        fieldName: creditorNameFieldName,
                        value: '',
                        hasError: true,
                        errorMessage: translate('validation.creditor-name.required'),
                    } as IFieldState),
                );
                isValid = false;
            } else if (creditorField?.hasError) {
                isValid = false;
            }
        }

        if (!isValid) return;

        try {
            setSaveError(false);
            isSavingRef.current = true;
            setIsSaving(true);

            if (kidValue) {
                const kidResult = await kidValidation(kidValue);
                if (!kidResult.valid) {
                    dispatch(
                        addOrUpdateFormField({
                            fieldName: kidNumberFieldName,
                            value: kidValue,
                            hasError: true,
                            errorMessage: translate(
                                kidResult.errorMessageKey ?? 'validation.error.consolidation.kid-number.format',
                            ),
                        } as IFieldState),
                    );
                    return;
                }
            }

            await onSave(consolidation.internalId);
        } catch {
            setSaveError(true);
        } finally {
            isSavingRef.current = false;
            setIsSaving(false);
        }
    };

    const kidValidation = (value?: string): Promise<ValidationResultType> => {
        const matchingItem = finalComplementaryConsolidationsRef.current.find((item) => item.kidNumber === value);
        const accountNumber = matchingItem?.accountNumber;

        return validateKidService(apiKey, proxyBaseUrl, value, accountNumber).then((valid) => ({
            valid,
            errorMessageKey: valid ? undefined : 'validation.error.consolidation.kid-number.format',
        }));
    };

    const kidValidationProps = (): FieldValidationProps => ({
        format: '^\\d{4,25}$',
        formatErrorKey: 'validation.error.consolidation.kid-number.format',
        customValidation: kidValidation,
    });

    return (
        <CardContainer isSaved={isSaved} data-testid={`loan-card-${consolidation.internalId}`}>
            <CardHeader onClick={handleHeaderClick}>
                <CardHeaderLeft>
                    {isSaved ? (
                        <Icon icon={faCheck} />
                    ) : isPartial ? (
                        <Icon icon={faChartPieSimple} />
                    ) : (
                        <Coins hideBackground={true} />
                    )}
                    <CardTextWrapper>
                        <Typography variant="subtitle1">{getTitle(consolidation)}</Typography>
                        {!isSaved && <Typography variant="caption">{getCaption(consolidation)}</Typography>}
                    </CardTextWrapper>
                </CardHeaderLeft>
                <button
                    type="button"
                    aria-label="Toggle details"
                    style={{ border: 'none', backgroundColor: 'white', cursor: 'pointer' }}>
                    <FontIcon
                        icon={
                            expanded && !isSaved ? (faChevronUp as IconDefinition) : (faChevronDown as IconDefinition)
                        }
                        size="xs"
                        cursor={'pointer'}
                    />
                </button>
            </CardHeader>

            {isPartial && !isSaved && (
                <Chip label={translate('field.partial-consolidation.label')} color="info" sx={{ mt: 1 }} />
            )}

            {expanded && !isSaved && (
                <>
                    {consolidation.creditorName ? (
                        <Group>
                            <IntegerField
                                name={accountNumberFieldName}
                                label={translate('field.consolidation.account-number')}
                                validation={{
                                    format: '^\\d{11}$',
                                    formatErrorKey: 'validation.error.account-number.format',
                                }}
                            />
                            <IntegerField
                                name={kidNumberFieldName}
                                label={translate('field.consolidation.kid-number.optional')}
                                validation={kidValidationProps()}
                            />
                        </Group>
                    ) : (
                        <Group>
                            <TextInputField
                                name={creditorNameFieldName}
                                label={translate('field.consolidation.creditor-name')}
                            />
                            <IntegerField
                                name={accountNumberFieldName}
                                label={translate('field.consolidation.account-number')}
                                validation={{
                                    format: '^\\d{11}$',
                                    formatErrorKey: 'validation.error.account-number.format',
                                }}
                            />
                            <IntegerField
                                name={kidNumberFieldName}
                                label={translate('field.consolidation.kid-number.optional')}
                                validation={kidValidationProps()}
                            />
                        </Group>
                    )}
                    <SaveButtonWrapper>
                        <Button variant="outlined" onClick={handleSave} fill disabled={isSaving || hasFieldError}>
                            {translate('field.consolidation.save-button')}
                            {isSaving && <Spinner size="sm" buttonLoadingStyles={true} />}
                        </Button>
                    </SaveButtonWrapper>
                </>
            )}
        </CardContainer>
    );
};

export default ConsolidationLoanCard;
